import { NavigationContainer } from '@react-navigation/native';
import materialIcons from '@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/MaterialIcons.ttf?url';
import { createRoot } from 'react-dom/client';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { recordBootPhase, timePhase } from '@/lib/boot-timings';
import { navigationRef } from './shims/navigation-ref';
import { initSqlite } from './shims/expo-sqlite';
import { initVfs } from './shims/vfs';

/**
 * Web entry point.
 *
 * Storage is brought up *before* the app module is imported. `db/client.ts`
 * opens the database at module scope — the synchronous contract expo-sqlite
 * has on native — so the wasm engine and the restored file system have to
 * exist first. That is why the root layout is imported dynamically below
 * rather than at the top of this file.
 */
/**
 * `@expo/vector-icons` draws glyphs as text in an icon font. Expo's native
 * build registers that font itself; on web it has to be added to the document
 * or every icon renders as a missing-glyph box.
 */
async function loadIconFont(): Promise<void> {
  // The web build of @expo/vector-icons asks for the family "material";
  // "MaterialIcons" is registered too so either naming resolves.
  try {
    await Promise.all(
      ['material', 'MaterialIcons'].map(async (family) => {
        const face = new FontFace(family, `url(${materialIcons})`);
        await face.load();
        document.fonts.add(face);
      }),
    );
  } catch (e) {
    console.warn('[icons] MaterialIcons failed to load', e);
  }
}

/**
 * Startup watchdog.
 *
 * Storage opens are now bounded, but something else can still stall — the
 * wasm engine, a chunk fetch on a bad connection. A child staring at a blank
 * screen with no explanation is the worst possible failure for this app, so
 * the splash starts reporting after ten seconds and offers a reload after
 * twenty-five rather than waiting silently forever.
 */
function startWatchdog(): () => void {
  const sub = document.querySelector('#splash .sub');

  const slow = setTimeout(() => {
    if (sub) sub.textContent = 'Nog besig… / Still working…';
  }, 10000);

  const stuck = setTimeout(() => {
    const splash = document.getElementById('splash');
    if (!splash) return;
    if (sub) {
      sub.textContent = 'Dit neem langer as gewoonlik. / This is taking longer than usual.';
    }
    const retry = document.createElement('button');
    retry.textContent = 'Probeer weer / Reload';
    retry.setAttribute(
      'style',
      'margin-top:8px;padding:12px 20px;font-size:16px;border-radius:10px;border:0;' +
        'background:#4BA3D3;color:#fff;font-family:inherit',
    );
    retry.onclick = () => window.location.reload();
    splash.append(retry);
  }, 25000);

  return () => {
    clearTimeout(slow);
    clearTimeout(stuck);
  };
}

async function start() {
  const stopWatchdog = startWatchdog();
  const bootStarted = Date.now();

  // Timed individually so a slow launch can be attributed rather than
  // guessed at: there is no console to read on an installed home-screen app.
  await Promise.all([
    timePhase('file store', initVfs),
    timePhase('database', initSqlite),
    timePhase('icon font', loadIconFont),
  ]);

  // Deliberately after the awaits, not alongside them: importing this module
  // evaluates `db/client.ts`, which opens the database at module scope, so it
  // cannot run before `initSqlite()` has resolved.
  const { default: RootLayout } = await timePhase('app bundle', () => import('../app/_layout'));

  recordBootPhase('total to render', Date.now() - bootStarted);
  stopWatchdog();

  createRoot(document.getElementById('root')!).render(
    <SafeAreaProvider>
      <NavigationContainer ref={navigationRef}>
        <RootLayout />
      </NavigationContainer>
    </SafeAreaProvider>,
  );
}

void start().catch((error: unknown) => {
  console.error('[startup] failed', error);
  // The splash is painted by index.html and normally removed once the app
  // mounts. If startup failed it never will, so it has to come down here or
  // the error below is rendered underneath it and nobody ever sees it.
  document.getElementById('splash')?.remove();
  const root = document.getElementById('root');
  if (!root) return;
  // The child cannot be left looking at a blank screen with no explanation.
  root.textContent = '';
  const panel = document.createElement('div');
  panel.setAttribute('style', 'font-family:system-ui;padding:24px;text-align:center');
  const af = document.createElement('p');
  af.textContent = 'Kon nie die program laai nie.';
  const en = document.createElement('p');
  en.setAttribute('style', 'opacity:.6');
  en.textContent = 'Could not start the app.';
  const detail = document.createElement('pre');
  detail.setAttribute('style', 'white-space:pre-wrap;opacity:.6;font-size:12px');
  detail.textContent = String(error);
  panel.append(af, en, detail);
  root.append(panel);
});
